import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboardIcon, BellIcon, CheckSquareIcon, FileTextIcon, FileIcon, ImageIcon, FileSpreadsheetIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon, MoreHorizontalIcon, FilterIcon, SearchIcon, PlusIcon, PrinterIcon, DownloadIcon, TrashIcon, ListChecksIcon, CopyCheckIcon, EyeIcon } from 'lucide-react';
import { isMobile, mobileDebounce } from '../utils/deviceDetection';
export const RequestManagement = () => {
  const features = [{
    title: 'Automated Reminders',
    description: 'Smart follow-ups that actually get responses.',
    icon: <BellIcon className="h-6 w-6" data-id="element-456" />
  }, {
    title: 'Itemized Checklists',
    description: 'Send clients exactly what you need with clear, organized lists.',
    icon: <ListChecksIcon className="h-6 w-6" data-id="element-457" />
  }, {
    title: 'Request Templates',
    description: 'Save time with reusable templates for common document sets.',
    icon: <CopyCheckIcon className="h-6 w-6" data-id="element-458" />
  }, {
    title: 'Unified Dashboard',
    description: 'Track all requests, uploads, and sent files in one place.',
    icon: <LayoutDashboardIcon className="h-6 w-6" data-id="element-459" />
  }];
  // Sample document request data
  const documentRequests = [{
    id: 1,
    name: 'Binder Subjectivities',
    type: 'invoice',
    status: 'verified',
    date: 'Jan 15, 2024',
    dueDate: 'Jan 20, 2024',
    client: 'HarborPoint Specialty'
  }, {
    id: 2,
    name: 'Visa Docs Checklist',
    type: 'certificate',
    status: 'complete',
    date: 'Jan 16, 2024',
    dueDate: 'Jan 22, 2024',
    client: 'Summit Immigration'
  }, {
    id: 3,
    name: 'Vendor Onboarding',
    type: 'document',
    status: 'changes',
    date: 'Jan 17, 2024',
    dueDate: 'Jan 25, 2024',
    client: 'Atlas Freight Lines'
  }, {
    id: 4,
    name: 'Agreement & Forms',
    type: 'id',
    status: 'inprogress',
    date: 'Jan 18, 2024',
    dueDate: 'Jan 30, 2024',
    client: 'ClearSign Solutions'
  }];
  // State for active filter
  const [activeFilter, setActiveFilter] = useState('all');
  // State for tracking expanded accordion rows - initialize with ID 1 expanded by default
  const [expandedRows, setExpandedRows] = useState({
    1: true
  });
  // Filter options
  const filterOptions = [{
    id: 'all',
    label: 'All Requests'
  }, {
    id: 'verified',
    label: 'Verified'
  }, {
    id: 'complete',
    label: 'Complete'
  }, {
    id: 'changes',
    label: 'Changes Requested'
  }, {
    id: 'inprogress',
    label: 'In Progress'
  }];
  // Filter documents based on active filter
  const filteredDocuments = activeFilter === 'all' ? documentRequests : documentRequests.filter(doc => doc.status === activeFilter);
  // Get icon based on document type
  const getDocumentIcon = type => {
    switch (type) {
      case 'invoice':
        return <FileTextIcon className="h-5 w-5" data-id="element-460" />;
      case 'certificate':
        return <FileIcon className="h-5 w-5" data-id="element-461" />;
      case 'id':
        return <ImageIcon className="h-5 w-5" data-id="element-462" />;
      default:
        return <FileSpreadsheetIcon className="h-5 w-5" data-id="element-463" />;
    }
  };
  // Get status details
  const getStatusDetails = status => {
    switch (status) {
      case 'verified':
        return {
          color: 'bg-blue-500',
          textColor: 'text-emerald-500',
          bgColor: 'bg-emerald-50',
          label: 'Verified',
          icon: <CheckCircleIcon className="h-4 w-4" data-id="element-464" />
        };
      case 'complete':
        return {
          color: 'bg-blue-500',
          textColor: 'text-blue-500',
          bgColor: 'bg-blue-50',
          label: 'Complete',
          icon: <CheckSquareIcon className="h-4 w-4" data-id="element-465" />
        };
      case 'changes':
        return {
          color: 'bg-transparent',
          textColor: 'text-amber-500',
          bgColor: 'bg-amber-50',
          label: 'Changes Requested',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-466" />
        };
      case 'inprogress':
        return {
          color: 'bg-transparent',
          textColor: 'text-purple-500',
          bgColor: 'bg-purple-50',
          label: 'In Progress',
          icon: <ClockIcon className="h-4 w-4" data-id="element-467" />
        };
      default:
        return {
          color: 'bg-gray-500',
          textColor: 'text-gray-500',
          bgColor: 'bg-gray-50',
          label: 'Unknown',
          icon: <FileIcon className="h-4 w-4" data-id="element-468" />
        };
    }
  };
  // Simplified animation variants for mobile
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: isMobile() ? 0.05 : 0.1,
        delayChildren: isMobile() ? 0.1 : 0.3 // Less delay on mobile
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: isMobile() ? 10 : 20 // Smaller distance on mobile
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: isMobile() ? 'tween' : 'spring',
        stiffness: isMobile() ? 50 : 100,
        duration: isMobile() ? 0.3 : undefined // Explicit duration for mobile
      }
    }
  };
  return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#FCFCFD] relative overflow-hidden" data-id="element-469">
      {/* Cinematic frame markers - hidden on mobile */}
      {!isMobile() && <>
          <div className="absolute inset-x-0 top-0 h-[1px] bg-[#2E6BFF]/10" data-id="element-470"></div>
          <div className="absolute inset-x-0 bottom-0 h-[1px] bg-[#2E6BFF]/10" data-id="element-471"></div>
        </>}
      {/* Background elements - static on mobile */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" data-id="element-472">
        {!isMobile() ? <motion.div className="absolute w-[30vw] h-[30vw] rounded-full bg-gradient-to-b from-[#F0F5FF]/50 to-transparent blur-[60px]" style={{
        top: '33%',
        right: '25%',
        transform: 'translate(0, -50%)',
        minWidth: '200px',
        minHeight: '200px'
      }} animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 12,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'easeInOut'
      }} data-id="element-473" /> : <div className="absolute w-[30vw] h-[30vw] rounded-full bg-gradient-to-b from-[#F0F5FF]/50 to-transparent blur-[60px]" style={{
        top: '33%',
        right: '25%',
        transform: 'translate(0, -50%)',
        minWidth: '200px',
        minHeight: '200px',
        opacity: 0.3
      }} data-id="element-474" />}
      </div>
      <div className="max-w-7xl mx-auto relative z-10" data-id="element-475">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-476">
          <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-[#0B1220] to-[#2E6BFF] bg-clip-text text-transparent" data-id="element-477">
            Smart Request Management
          </h2>
          <p className="mt-4 text-lg text-[#5A6376] max-w-2xl mx-auto" data-id="element-478">
            Streamline your document collection process with intelligent tools
            designed for efficiency.
          </p>
        </motion.div>
        {/* Dashboard Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-16 bg-white rounded-xl shadow-lg border border-[#E7EAF0]/50 overflow-hidden" data-id="element-479">
          {/* Dashboard Header with Filters */}
          <div className="p-3 sm:p-4 border-b border-[#E7EAF0]" data-id="element-480">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-2" data-id="element-481">
              <h3 className="text-lg font-semibold text-[#0B1220]" data-id="element-482">
                Document Requests
              </h3>
              <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-483">
                <div className="relative flex-1 max-w-[calc(100%-44px)] sm:flex-grow-0 sm:max-w-xs" data-id="element-484">
                  <input type="text" placeholder="Search requests..." className="pl-9 pr-4 py-1.5 text-sm border border-[#E7EAF0] rounded-full focus:outline-none focus:ring-2 focus:ring-[#2E6BFF]/20 focus:border-[#2E6BFF] w-full" aria-label="Search requests" data-id="element-485" />
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6376]" data-id="element-486" />
                </div>
                <motion.button className="inline-flex items-center justify-center p-1.5 rounded-full bg-[#F0F5FF] text-[#2E6BFF] hover:bg-[#E1EBFF] transition-colors flex-shrink-0" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} aria-label="Filter options" data-id="element-487">
                  <FilterIcon className="h-4 w-4" data-id="element-488" />
                </motion.button>
              </div>
            </div>
            {/* Filter Tabs */}
          </div>
          {/* Document Cards */}
          <div className="divide-y divide-[#E7EAF0]" data-id="element-489">
            {/* Column Headers */}
            <div className="hidden sm:block p-4 sm:p-6 bg-[#F8FAFF]" data-id="element-490">
              <div className="flex flex-row items-center w-full text-sm font-medium text-[#5A6376]" data-id="element-491">
                <div className="sm:w-[4%]" data-id="element-492">
                  <span className="sr-only" data-id="element-493">Status</span>
                </div>
                <div className="sm:w-[22%] sm:border-r sm:border-[#E7EAF0] sm:pr-4" data-id="element-494">
                  <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-[#E1EBFF]/50 hover:text-[#2E6BFF] transition-colors cursor-pointer group" data-id="element-495">
                    <span data-id="element-496">Request</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#5A6376] group-hover:text-[#2E6BFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-497">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" data-id="element-498" />
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#5A6376] group-hover:text-[#2E6BFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-499">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m-4 4v8m0 0l-4-4m4 4l4-4" data-id="element-500" />
                    </svg>
                  </button>
                </div>
                <div className="sm:w-[22%] sm:border-r sm:border-[#E7EAF0] sm:px-4" data-id="element-501">
                  <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-[#E1EBFF]/50 hover:text-[#2E6BFF] transition-colors cursor-pointer group" data-id="element-502">
                    <span data-id="element-503">Client</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#5A6376] group-hover:text-[#2E6BFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-504">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" data-id="element-505" />
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#5A6376] group-hover:text-[#2E6BFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-506">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m-4 4v8m0 0l-4-4m4 4l4-4" data-id="element-507" />
                    </svg>
                  </button>
                </div>
                <div className="sm:w-[23%] sm:border-r sm:border-[#E7EAF0] sm:px-4" data-id="element-508">
                  <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-[#E1EBFF]/50 hover:text-[#2E6BFF] transition-colors cursor-pointer group" data-id="element-509">
                    <span data-id="element-510">Status</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#5A6376] group-hover:text-[#2E6BFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-511">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" data-id="element-512" />
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-[#5A6376] group-hover:text-[#2E6BFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-513">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m-4 4v8m0 0l-4-4m4 4l4-4" data-id="element-514" />
                    </svg>
                  </button>
                </div>
                <div className="sm:w-[14%] sm:px-4" data-id="element-515">
                  <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-[#E1EBFF]/50 hover:text-[#2E6BFF] transition-colors cursor-pointer" data-id="element-516">
                    <span data-id="element-517">Updated</span>
                  </button>
                </div>
                <div className="sm:w-[15%] sm:pl-4 sm:border-l sm:border-[#E7EAF0]" data-id="element-518">
                  <button className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-[#E1EBFF]/50 hover:text-[#2E6BFF] transition-colors cursor-pointer" data-id="element-519">
                    <span data-id="element-520">Actions</span>
                  </button>
                </div>
              </div>
            </div>
            {filteredDocuments.map(doc => {
            const statusDetails = getStatusDetails(doc.status);
            return <motion.div key={doc.id} className="p-4 sm:p-6 hover:bg-[#F8FAFF] transition-colors border-b border-[#E7EAF0] last:border-b-0" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} whileHover={{
              backgroundColor: '#F0F5FF',
              transition: {
                duration: 0.2
              }
            }} data-id="element-521">
                  {/* Mobile Accordion View */}
                  <div className="block sm:hidden" data-id="element-522">
                    <div className="flex items-center justify-between w-full cursor-pointer" onClick={() => {
                  // Toggle expanded state for this document
                  setExpandedRows(prev => ({
                    ...prev,
                    [doc.id]: !prev[doc.id]
                  }));
                }} data-id="element-523">
                      <div className="flex items-center space-x-3" data-id="element-524">
                        <div className={`w-2.5 h-2.5 rounded-full ${statusDetails.color}`} aria-hidden="true" data-id="element-525"></div>
                        <div data-id="element-526">
                          <h4 className="font-medium text-[#0B1220]" data-id="element-527">
                            {doc.name}
                          </h4>
                          <p className="text-xs text-[#5A6376]" data-id="element-528">{doc.client}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3" data-id="element-529">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full ${statusDetails.bgColor} ${statusDetails.textColor} text-xs font-medium`} data-id="element-530">
                          {statusDetails.icon}
                          <span className="ml-1 whitespace-nowrap hidden xs:inline" data-id="element-531">
                            {statusDetails.label}
                          </span>
                        </span>
                        <motion.div animate={{
                      rotate: expandedRows[doc.id] ? 180 : 0
                    }} transition={{
                      duration: 0.3
                    }} className="text-[#5A6376]" data-id="element-532">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-533">
                            <polyline points="6 9 12 15 18 9" data-id="element-534"></polyline>
                          </svg>
                        </motion.div>
                      </div>
                    </div>
                    {/* Expandable content */}
                    <AnimatePresence data-id="element-535">
                      {expandedRows[doc.id] && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} className="overflow-hidden" data-id="element-536">
                          <div className="pt-4 mt-4 border-t border-[#E7EAF0] space-y-3" data-id="element-537">
                            <div className="flex justify-between" data-id="element-538">
                              <div data-id="element-539">
                                <p className="text-xs text-[#5A6376]" data-id="element-540">
                                  Contact
                                </p>
                                <p className="text-sm text-[#0B1220] font-medium" data-id="element-541">
                                  {doc.id === 1 ? 'John Smith' : doc.id === 2 ? 'Maria Rodriguez' : doc.id === 3 ? 'David Chen' : 'Sarah Johnson'}
                                </p>
                              </div>
                              <div data-id="element-542">
                                <p className="text-xs text-[#5A6376]" data-id="element-543">Status</p>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full ${statusDetails.bgColor} ${statusDetails.textColor} text-xs font-medium`} data-id="element-544">
                                  {statusDetails.label}
                                </span>
                              </div>
                            </div>
                            <div className="flex justify-between" data-id="element-545">
                              <div data-id="element-546">
                                <p className="text-xs text-[#5A6376]" data-id="element-547">
                                  Last Updated
                                </p>
                                <p className="text-sm text-[#0B1220]" data-id="element-548">
                                  {doc.id === 1 ? '10:32 am' : doc.id === 2 ? '3/7/25' : doc.id === 3 ? '2/12/25' : '1/18/25'}
                                </p>
                              </div>
                              <div data-id="element-549">
                                <p className="text-xs text-[#5A6376]" data-id="element-550">
                                  Due Date
                                </p>
                                <p className="text-sm text-[#0B1220]" data-id="element-551">
                                  {doc.date}
                                </p>
                              </div>
                            </div>
                            {/* Action buttons */}
                            <div className="flex items-center justify-center space-x-2 pt-2" data-id="element-552">
                              <motion.button className="p-1.5 text-[#475569] bg-[#F8FAFC] hover:bg-[#E2E8F0] rounded-full transition-colors border border-[#E7EAF0]" whileTap={{
                          scale: 0.95
                        }} aria-label="View document" data-id="element-553">
                                <EyeIcon className="h-4 w-4" data-id="element-554" />
                              </motion.button>
                              <motion.button className="p-1.5 text-[#475569] bg-[#F8FAFC] hover:bg-[#E2E8F0] rounded-full transition-colors border border-[#E7EAF0]" whileTap={{
                          scale: 0.95
                        }} aria-label="Print document" data-id="element-555">
                                <PrinterIcon className="h-4 w-4" data-id="element-556" />
                              </motion.button>
                              <motion.button className="p-1.5 text-[#475569] bg-[#F8FAFC] hover:bg-[#E2E8F0] rounded-full transition-colors border border-[#E7EAF0]" whileTap={{
                          scale: 0.95
                        }} aria-label="Download document" data-id="element-557">
                                <DownloadIcon className="h-4 w-4" data-id="element-558" />
                              </motion.button>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                  {/* Desktop Table View */}
                  <div className="hidden sm:block" data-id="element-559">
                    <div className="flex flex-row items-start sm:items-center w-full" data-id="element-560">
                      {/* Status Indicator - Slightly reduced */}
                      <div className="flex items-center mb-3 sm:mb-0 sm:w-[4%]" data-id="element-561">
                        <div className={`w-2.5 h-2.5 rounded-full ${statusDetails.color}`} aria-hidden="true" data-id="element-562"></div>
                      </div>
                      {/* Document Name - Slightly reduced */}
                      <div className="mb-3 sm:mb-0 sm:w-[22%] sm:border-r sm:border-[#E7EAF0] sm:pr-4" data-id="element-563">
                        <h4 className="font-medium text-[#0B1220] truncate" data-id="element-564">
                          {doc.name}
                        </h4>
                      </div>
                      {/* Client Column - Reduced width */}
                      <div className="mb-3 sm:mb-0 sm:w-[22%] sm:border-r sm:border-[#E7EAF0] sm:px-4" data-id="element-565">
                        <p className="text-sm text-[#0B1220] font-medium truncate" data-id="element-566">
                          {doc.client}
                        </p>
                        <p className="text-xs text-[#5A6376] truncate" data-id="element-567">
                          {doc.id === 1 ? 'John Smith' : doc.id === 2 ? 'Maria Rodriguez' : doc.id === 3 ? 'David Chen' : 'Sarah Johnson'}
                        </p>
                      </div>
                      {/* Status Column - Increased width */}
                      <div className="mb-3 sm:mb-0 sm:w-[23%] sm:border-r sm:border-[#E7EAF0] sm:px-4" data-id="element-568">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full ${statusDetails.bgColor} ${statusDetails.textColor} text-xs font-medium`} data-id="element-569">
                          {statusDetails.icon}
                          <span className="ml-1.5 whitespace-nowrap" data-id="element-570">
                            {statusDetails.label}
                          </span>
                        </span>
                      </div>
                      {/* Updated Information - Matched to header width */}
                      <div className="text-sm text-[#5A6376] mb-3 sm:mb-0 sm:w-[14%] sm:px-4" data-id="element-571">
                        <span data-id="element-572">
                          {doc.id === 1 ? '10:32 am' : doc.id === 2 ? '3/7/25' : doc.id === 3 ? '2/12/25' : '1/18/25'}
                        </span>
                      </div>
                      {/* Action Buttons - Increased width */}
                      <div className="flex items-center space-x-2 sm:w-[15%] ml-auto sm:ml-0 justify-end sm:pl-4 sm:border-l sm:border-[#E7EAF0]" data-id="element-573">
                        <motion.button className="p-1.5 text-[#475569] bg-[#F8FAFC] hover:bg-[#E2E8F0] rounded-full transition-colors border border-[#E7EAF0]" whileHover={{
                      y: -2
                    }} whileTap={{
                      scale: 0.95
                    }} aria-label="View document" data-id="element-574">
                          <EyeIcon className="h-4 w-4" data-id="element-575" />
                        </motion.button>
                        <motion.button className="p-1.5 text-[#475569] bg-[#F8FAFC] hover:bg-[#E2E8F0] rounded-full transition-colors border border-[#E7EAF0]" whileHover={{
                      y: -2
                    }} whileTap={{
                      scale: 0.95
                    }} aria-label="Print document" data-id="element-576">
                          <PrinterIcon className="h-4 w-4" data-id="element-577" />
                        </motion.button>
                        <motion.button className="p-1.5 text-[#475569] bg-[#F8FAFC] hover:bg-[#E2E8F0] rounded-full transition-colors border border-[#E7EAF0]" whileHover={{
                      y: -2
                    }} whileTap={{
                      scale: 0.95
                    }} aria-label="Download document" data-id="element-578">
                          <DownloadIcon className="h-4 w-4" data-id="element-579" />
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </motion.div>
        {/* Feature Cards Section */}
        <motion.div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" variants={container} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: isMobile() ? '-50px' : '-100px' // Smaller margin on mobile
      }} data-id="element-580">
          {features.map((feature, index) => <motion.div key={index} variants={item} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-[#E7EAF0]/50" whileHover={isMobile() ? {} : {
          y: -5,
          boxShadow: '0 20px 40px rgba(9,16,29,0.1)'
        }} data-id="element-581">
              <div className="bg-gradient-to-br from-[#F0F5FF] to-[#E1EBFF] w-12 h-12 rounded-full flex items-center justify-center mb-4 text-[#2E6BFF] shadow-[0_4px_10px_rgba(46,107,255,0.15)]" data-id="element-582">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-[#0B1220] mb-2" data-id="element-583">
                {feature.title}
              </h3>
              <p className="text-[#5A6376]" data-id="element-584">{feature.description}</p>
            </motion.div>)}
        </motion.div>
        {/* Book a Call Button */}
        <motion.div className="mt-16 text-center" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.5
      }} data-id="element-585">
          <motion.a href="https://calendly.com/pigeondocuments" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white bg-[#2E6BFF] hover:bg-[#1E5BFF] transition-colors duration-300 shadow-[0_8px_16px_rgba(46,107,255,0.25)]" whileHover={{
          y: -2,
          boxShadow: '0 12px 20px rgba(46,107,255,0.3)'
        }} whileTap={{
          scale: 0.98
        }} data-id="element-586">
            Book a Call
          </motion.a>
        </motion.div>
      </div>
    </section>;
};